/**
 * Cross-tab auth-state sync.
 *
 * The `user` object (incl. `godModeSession`) lives in per-window memory, while
 * the session (refresh cookie) is shared across tabs. When God Mode is entered
 * or exited in one tab, other tabs must re-fetch `/me` or they keep rendering
 * a stale workspace. This module broadcasts a lightweight, payload-free signal
 * so AuthContext can call `refreshUser()` in the other tabs.
 *
 * Uses BroadcastChannel; falls back to a localStorage key bump (the `storage`
 * event only fires in OTHER tabs, which is exactly what we want).
 */

const CHANNEL_NAME = 'aa-auth-sync';
const STORAGE_KEY = 'aa-auth-sync';

/** Per-tab id so a tab can ignore its own broadcasts (both transports). */
const SENDER_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface AuthSyncMessage {
  type: 'auth-changed';
  senderId: string;
  reason: 'god-mode-enter' | 'god-mode-exit';
  at: number;
}

const isAuthSyncMessage = (value: unknown): value is AuthSyncMessage =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'auth-changed' &&
  typeof (value as { senderId?: unknown }).senderId === 'string';

const hasBroadcastChannel = (): boolean => typeof BroadcastChannel !== 'undefined';

/** Notify other tabs that the auth/team context changed. Never throws. */
export const broadcastAuthChanged = (reason: AuthSyncMessage['reason']): void => {
  const message: AuthSyncMessage = { type: 'auth-changed', senderId: SENDER_ID, reason, at: Date.now() };
  try {
    if (hasBroadcastChannel()) {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
    }
  } catch {
    // Best-effort signal; visibility re-sync is the safety net.
  }
};

/** Subscribe to auth-changed signals from other tabs. Returns an unsubscribe fn. */
export const subscribeToAuthChanged = (onChange: (message: AuthSyncMessage) => void): (() => void) => {
  if (hasBroadcastChannel()) {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (isAuthSyncMessage(event.data) && event.data.senderId !== SENDER_ID) onChange(event.data);
    };
    return () => channel.close();
  }

  const handler = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const parsed: unknown = JSON.parse(event.newValue);
      if (isAuthSyncMessage(parsed) && parsed.senderId !== SENDER_ID) onChange(parsed);
    } catch {
      // ignore malformed payloads
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
};

// ---------------------------------------------------------------------------
// Same-tab stale-team-context signal
// ---------------------------------------------------------------------------

const RESYNC_EVENT = 'aa-auth-resync';
const RESYNC_MIN_INTERVAL_MS = 10_000;
let lastResyncRequestAt = 0;

/**
 * Ask AuthContext to re-fetch `/me` (e.g. the API said the saved God Mode team is no
 * longer in scope). Debounced so a burst of failing requests triggers ONE re-sync.
 */
export const requestAuthResync = (): void => {
  const now = Date.now();
  if (now - lastResyncRequestAt < RESYNC_MIN_INTERVAL_MS) return;
  lastResyncRequestAt = now;
  window.dispatchEvent(new Event(RESYNC_EVENT));
};

/** Subscribe to same-tab re-sync requests. Returns an unsubscribe fn. */
export const subscribeToAuthResync = (onResync: () => void): (() => void) => {
  window.addEventListener(RESYNC_EVENT, onResync);
  return () => window.removeEventListener(RESYNC_EVENT, onResync);
};
